/**
 * Tutor card for the management {@link DataTable}.
 * Compact layout: name, situation, optional contact and onboarding milestones.
 */
import * as React from 'react';
import { User } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import {
  TUTOR_REMOTE_MILESTONES as TUTOR_MILESTONES,
  type TutorRemoteTabView,
} from '@/components/tutors/tutor-configs';
import type { TutorRecord } from '@/types/remote/tutor.types';
import {
  canShowArchiveRow,
  canShowReactivateRow,
  deriveTutorStatusConfigs,
  deriveTutorContact,
  getTutorRemoteMilestoneStatus as getTutorMilestoneStatus,
  type TutorRowPageActions,
} from './tutor-table-utils';

/** Props for {@link TutorCard}. */
interface TutorCardProps {
  record: TutorRecord;
  onView?: (id: string) => void;
  onEdit?: (id: string, record: TutorRecord) => void;
  onViewSessions?: (record: TutorRecord) => void;
  onViewReports?: (record: TutorRecord) => void;
  pageActions?: TutorRowPageActions;
  activeTab: TutorRemoteTabView;
  onArchive?: (userId: string) => void;
  onReactivate?: (userId: string) => void;
}

/** Tutor card for the management {@link DataTable}. */
export const TutorCard: React.FC<TutorCardProps> = ({
  record,
  onView,
  onEdit,
  onViewSessions,
  onViewReports,
  pageActions,
  activeTab,
  onArchive,
  onReactivate,
}) => {
  const { statusConfig, StatusIcon, situationConfig, SituationIcon } =
    deriveTutorStatusConfigs(record);

  const primaryContact = deriveTutorContact(record);

  const sections: Record<string, Record<string, unknown>> = record.sections ?? {};
  const basicInfo = sections['basic_information'];
  const firstName = record.user?.firstName ?? (basicInfo?.firstName as string | undefined) ?? '—';
  const lastName = record.user?.lastName ?? (basicInfo?.lastName as string | undefined) ?? '—';

  const userStatus = record.user?.status;
  const showArchive = canShowArchiveRow(userStatus, pageActions?.canArchiveRow);
  const showReactivate = canShowReactivateRow(userStatus, activeTab, pageActions?.canReactivateRow);

  function handleArchive(event: React.MouseEvent): void {
    event.stopPropagation();
    onArchive?.(record.userId);
  }

  function handleReactivate(event: React.MouseEvent): void {
    event.stopPropagation();
    onReactivate?.(record.userId);
  }

  const handleClick = () => {
    onView?.(record.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onView?.(record.id);
    }
  };

  return (
    <TableCard onClick={handleClick} onKeyDown={handleKeyDown} role="button" tabIndex={0}>
      <div className="data-card-content">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-[color-mix(in_srgb,var(--color-1-nuance-1)_12%,transparent)]">
            <User className="w-5 h-5 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <span className="font-semibold text-foreground text-base truncate">
              {firstName} {lastName}
            </span>
            <div className="flex items-center flex-wrap gap-2 mt-0.5">
              {statusConfig && (
                <LabelBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              )}
              <LabelBadge
                label={situationConfig.label}
                icon={SituationIcon}
                variant={situationConfig.badgeVariant}
                color={situationConfig.badgeColor}
                size="sm"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2 min-w-0">
            <primaryContact.icon
              className="w-4 h-4 flex-shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
            <span className="text-sm text-foreground truncate">{primaryContact.value}</span>
          </div>
          {primaryContact.secondaryValue && (
            <span className="text-xs text-muted-foreground pl-6">
              {primaryContact.secondaryValue}
            </span>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-3">
          {TUTOR_MILESTONES.map((m) => {
            const mStatus = getTutorMilestoneStatus(record.sections, m.key);
            const Icon = m.icon;
            return (
              <div key={m.key} className="flex flex-col items-center gap-0.5" title={m.label}>
                <Icon
                  className="w-4 h-4"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                />
                <span
                  className="text-[10px]"
                  style={{ color: mStatus === 'validated' ? m.validatedColor : m.pendingColor }}
                >
                  {m.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(record.id);
            }}
          />
          {pageActions?.canEditRow && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(record.userId, record);
              }}
            />
          )}
          {pageActions?.canViewSessions !== false && onViewSessions && (
            <Button
              preset="row-view-sessions"
              onClick={(e) => {
                e.stopPropagation();
                onViewSessions(record);
              }}
            />
          )}
          {pageActions?.canViewReports !== false && onViewReports && (
            <Button
              preset="row-view-reports"
              onClick={(e) => {
                e.stopPropagation();
                onViewReports(record);
              }}
            />
          )}
          {showArchive ? <Button preset="row-archive" onClick={handleArchive} /> : null}
          {showReactivate ? <Button preset="row-reactivate" onClick={handleReactivate} /> : null}
        </div>
      </div>
    </TableCard>
  );
};
